<template>
  <div v-bind="$attrs">
    <el-dialog
      v-model="dialogVisible"
      title=""
      width="1100px"
      :show-close="false"
      :close-on-click-modal="false"
      modal-class="checkout-dialog"
      destroy-on-close
      append-to-body
    >
      <!-- Custom Header -->
      <template #header>
        <div
          class="flex justify-between items-center px-6 py-4 bg-[#004879] text-white rounded-t-[10px]"
        >
          <div class="flex items-center gap-2">
            <img
              src="@/assets/MyPatient/input_order.svg"
              alt="Order"
              class="w-6 h-6 brightness-0 invert"
            />
            <span class="font-semibold text-[20px] leading-normal"
              >{{ configMode ? 'Configure Order' : 'Checkout' }}</span
            >
          </div>
          <button
            class="bg-transparent border-none p-0 cursor-pointer w-7 h-7"
            @click="closeDialog"
          >
            <img
              src="@/assets/MyPatient/close.svg"
              alt="Close"
              class="w-full h-full"
            />
          </button>
        </div>
      </template>

      <!-- Dialog Content -->
      <div class="dialog-content-wrapper">
        <div class="two-column-layout">
          <!-- Left Column: Step 2 -->
          <div class="left-column">
            <!-- Step 2: Order Handling -->
            <div class="step-title flex justify-between items-center">
              <div class="mb-4">
                Step 2: Order Handling
              </div>
            </div>

            <div id="step2" class="space-y-4 step-section step-two-container step-two-form-wrapper">
          <!-- Back to Shop Button -->
          <div class="mb-4">
            <button
              class="back-to-shop-btn"
              @click="goBack"
            >
              Back to Shop
            </button>
          </div>

          <!-- Promo Code Section (hidden in configMode) -->
          <div v-if="!configMode" class="promo-code-section mb-6">
            <div class="flex items-center gap-3">
              <span class="label">Promo Code:</span>
              <TextInput
                v-model="promoCode"
                size="medium"
                placeholder="Enter promo code"
                :disabled="isPromoCodeValid"
                :clearable="false"
                class="promo-input-wrapper checkout-kit-selector checkout-kit-selector-wide"
                @input="isShowPromoCodeValidationMessage = false"
              >
                <template v-if="isShowPromoCodeValidationMessage" #suffix>
                  <span class="promo-status-icon">
                    <span v-if="isPromoCodeValid" class="status-success">&#10003;</span>
                    <span v-else class="status-error">&#10005;</span>
                  </span>
                </template>
              </TextInput>
              <button
                v-if="!isPromoCodeValid"
                class="promo-apply-btn"
                :disabled="applyCouponLoading"
                @click="applyCoupon"
              >
                {{ applyCouponLoading ? '...' : 'Apply' }}
              </button>
              <button
                v-else
                class="promo-remove-btn"
                @click="removeCoupon"
              >
                Remove
              </button>
            </div>
            <div v-if="isShowPromoCodeValidationMessage" class="mt-2" style="padding-left: 183px;">
              <InlineNotification
                :theme="isPromoCodeValid ? 'info' : 'error'"
                :message="isPromoCodeValid ? 'The promo code you entered is applied.' : invalidCouponReason"
                :with_close_btn="false"
              />
            </div>
            <!-- bVibrant first order banner -->
            <div v-if="!bVibrantUsed && !promoCode && !isPromoCodeValid" class="mt-2" style="padding-left: 183px;">
              <InlineNotification
                theme="info"
                message="🎉 Enjoy up to $400 off your first order — use code"
                action_text="bVibrant"
                :with_close_btn="false"
                @action="applyBVibrantCoupon"
              />
            </div>
            <!-- Coupon exceeds warning -->
            <div v-if="couponExceedsVibrantSubtotal" class="mt-2" style="padding-left: 183px;">
              <InlineNotification
                theme="warning"
                message="Coupons apply only to the wholesale value of Vibrant Product. Any remaining coupon amount will not be retained or credited."
                :with_close_btn="false"
              />
            </div>
          </div>

          <!-- Editable Form View -->
          <div>
            <template v-if="hasBloodSamples || hasNonBloodSamples">
              <div class="mb-4 font-medium">Logistic:</div>
              <div class="flex flex-col gap-4 mb-6">
                <div v-if="hasBloodSamples" class="flex items-center gap-3">
                  <span class="label">Blood Kit:</span>
                  <SingleSelector
                    v-model="handling.bloodKit"
                    :options="bloodKitOptions"
                    size="medium"
                    class="checkout-kit-selector checkout-kit-selector-wide"
                  />
                </div>
                <div v-if="hasNonBloodSamples" class="flex items-center gap-3">
                  <span class="label">Non-Blood Kit:</span>
                  <SingleSelector
                    v-model="handling.nonBloodKit"
                    :options="nonBloodKitOptions"
                    size="medium"
                    class="checkout-kit-selector checkout-kit-selector-wide"
                  />
                </div>
              </div>
            </template>

            <ConciergeFee v-if="showConciergeFee" class="mt-2 mb-4" :concierge-fee-info="conciergeFeeInfo" />

            <InlineNotification
              v-if="showPracticeWarning && hasPracticeProduct && isPatientPaymentSelected"
              theme="warning"
              message="Practice products require patient email or phone number for order notification. Please provide at least one contact method below."
              class="warning-alert mb-4"
              @close="showPracticeWarning = false"
            />

            <template v-if="!configMode">
              <div class="mb-4 font-medium">Send Order Notification to Patient:</div>
              <div class="flex flex-col gap-4 mb-6">
                <div class="flex items-center gap-3">
                  <span class="label">Send email to:</span>
                  <TextInput
                    v-model="emailInput"
                    size="medium"
                    placeholder="examplemail@test.com"
                    :clearable="false"
                    class="checkout-kit-selector checkout-kit-selector-wide"
                    @blur="updateEmail"
                  />
                </div>
                <div class="flex items-center gap-3">
                  <span class="label">Send Text message to:</span>
                  <TextInput
                    v-model="phoneInput"
                    size="medium"
                    placeholder="Enter Phone Number"
                    :clearable="false"
                    class="checkout-kit-selector checkout-kit-selector-wide"
                    @blur="updatePhone"
                  />
                </div>
                <InlineNotification
                  v-if="showContactError"
                  theme="error"
                  message="Patient email or phone number is required for practice product orders with patient payment."
                  :with_close_btn="false"
                  class="mb-0"
                />
              </div>
            </template>

            <div class="mb-2 font-medium">Payment Method:</div>
            <!-- configMode: only Patient Pay Later -->
            <template v-if="configMode">
              <div class="flex flex-row mx-20 mb-4">
                <label class="pay-option">
                  <el-radio
                    v-model="handling.payment"
                    label="patient_pay_later"
                    style="width: 220px; text-align: center; text-wrap: wrap;"
                    disabled
                    >Patient Pay Later - Instruction Send to Patient</el-radio
                  >
                </label>
              </div>
              <div class="mt-4 ml-4 mr-4">
                <InlineNotification
                  theme="info"
                  message="Instructions will be sent to patient's contacts saved above."
                  :with_close_btn="false"
                  class="w-2/3 mb-3"
                />
                <DynamicColorResponsiveButton
                  class="w-full mt-4"
                  button_type="filled"
                  built_in_theme="primary"
                  button_border_radius="4px"
                  width_type="fill-whole"
                  display_name="Save"
                  @click-button="saveConfigOrder"
                />
              </div>
            </template>
            <!-- Normal mode: all payment options -->
            <template v-else>
              <div class="flex flex-row justify-around mx-20">
                <label class="pay-option">
                  <el-radio
                    v-model="handling.payment"
                    label="office_card_on_file"
                    style="width: 180px; text-align: center; text-wrap: wrap;"
                    >Provider Office Bill - Card on File</el-radio
                  >
                </label>
                <label class="pay-option">
                  <el-radio
                    v-model="handling.payment"
                    label="patient_pay_now"
                    style="width: 180px; text-align: center; text-wrap: wrap;"
                    >Patient Pay Now - Collect Card Info</el-radio
                  >
                </label>
                <label class="pay-option">
                  <el-radio
                    v-model="handling.payment"
                    label="patient_pay_later"
                    style="width: 220px; text-align: center; text-wrap: wrap;"
                    >Patient Pay Later - Instruction Send to Patient</el-radio
                  >
                </label>
              </div>
              <div class="mt-4 ml-4 mr-4">
                <PaymentMethod
                  :paymentMethod="paymentMethodMapped"
                  :confirmDisabled="hasPracticeButNoContact || showContactError"
                  :patientId="patientId"
                  :customerId="customerId"
                  :isClinic="isClinic"
                  :chargeAmount="chargeAmountForPayment"
                  :chargeItemsCount="cart.length"
                  :orderPriceList="orderPriceList"
                  :isUsCustomer="isUsCustomer"
                  :stripeAccount="hasMerchandise ? 'ZB' : ''"
                  :clinicId="clinicId"
                  :currency="currency"
                  :showHsaWarning="hasMerchandise"
                  :useCoupon="useCoupon"
                  :needSampleId="true"
                  :isShowAchPayment="true"
                  :sampleList="[]"
                  :isLoading="isPlacingOrder"
                  pay-order-without-apply
                  :showDisclaimer="false"
                  @completePatientPayNow="onCompletePatientPayNow"
                  @completeCustomerPay="onCompleteCustomerPay"
                  @completePatientPayLater="onCompletePatientPayLater"
                >
                  <InlineNotification
                    theme="info"
                    message="Instructions will be sent to patient's contacts saved above."
                    :with_close_btn="false"
                    class="w-2/3 mb-3"
                  />
                </PaymentMethod>
              </div>
            </template>
          </div>
            </div>
          </div>

          <!-- Right Column: Order Summary -->
          <div 
            :class="[
              'right-column',
              'order-summary-sidebar',
              { 'order-summary-expanded': isOrderSummaryExpanded },
            ]"
          >
            <div class="order-summary-panel">
              <VaOrderSummary
                ref="vaOrderSummaryRef"
                class="order-summary-embedded"
                :order-draft="{}"
                :show-accessories="false"
                :show-item-type-group="true"
                :expanded="isOrderSummaryExpanded"
                @toggle-expand="toggleOrderSummaryExpand"
              />
              <!-- Checkout Buttons for EHR -->
              <div class="ehr-checkout-buttons">
                <DynamicColorResponsiveButton
                  class="w-full mb-2"
                  button_type="outlined"
                  built_in_theme="primary"
                  button_border_radius="4px"
                  width_type="fill-whole"
                  display_name="Back"
                  @click-button="goBack"
                />
                <!-- Place Order button hidden - order is placed via left panel -->
                <!-- <Button
                  color-type="dark-blue"
                  :disabled="isFeeLoading || isPlacingOrder"
                  @click="handlePlaceOrderClick"
                  class="w-full"
                  style-text="background: #20A8C3; border: none;"
                >
                  {{ isPlacingOrder ? 'Placing Order...' : 'Place Order' }}
                </Button> -->
              </div>
            </div>
          </div>
        </div>
      </div>

    </el-dialog>

    <PromoCodeOrBundleDialog
      :is-promo-code-or-bundle-dialog-visible="isPromoCodeOrBundleDialogVisible"
      :price-with-breakdown="pricesAfterCoupon.withBundleBreakdown"
      :price-without-breakdown="pricesAfterCoupon.withoutBundleBreakdown"
      @close="closePromoCodeOrBundleDialog"
      @user-chooses-bundle="userChoosesBundle"
      @user-chooses-promo-code="userChoosesPromoCode"
    />
  </div>
</template>

<script>
import NewOrderCartItems from './NewOrderCartItems.vue';
import PaymentMethod from '@/components/PaymentMethod/index.vue';
import VaOrderSummary from '@/views/va-orderTest/order-page/order-summary/index.vue';
import { mapState, mapGetters } from 'vuex';
import { processBloodOrNonBloodKit } from '@/utils/va-order-test/sampleTypes.js';
import { getInternationalShippingFee } from '@/api/va-order-page/pricing/shipping';
import { calculateTax } from '@/api/va-order-page/pricing/tax';
import { placeOrderV2, createTubeInfo, isValidConciergeFeeV2Api } from '@/api/va-order-page/orderTest';
import { getOrderSamplePriceMaps } from '@/components/PaymentMethod';
import { getLineItemsPayalod } from '@/views/va-orderTest/order-page/order-summary/SummaryAccessories/getOrderItemListFunc';
import { notify } from '@/utils/notify';
import { getShippingAddressLengthErrors, formatShippingLengthMessage } from '@/utils/shippingAddressLimits';

// Labels for the shipping-length toast (VP-18765), keyed by the session
// patient's address field names.
const SHIPPING_FIELD_LABELS = {
  streetAddress: 'Street Address',
  addressLine2: 'Address Line 2',
  city: 'City',
  state: 'State',
  zipcode: 'Zip Code',
  country: 'Country',
};
import { verifyCouponCodeApi, preCheckCouponUsageApi } from '@/api/va-order-page/pricing/item/promotion.js';
import { getVerifyCouponApiPayload, breakdownCouponEligibleBundles, handleShortcutAfterCouponRemoved, getPricesAfterCouponApplied } from '@/views/va-orderTest/checkout-page/price-summary/components/promoCode.js';
import PromoCodeOrBundleDialog from '@/views/va-orderTest/checkout-page/price-summary/components/PromoCodeOrBundleDialog.vue';
import { handleBestDealSuggestion } from '@/utils/va-order-test/bestDealSuggestion/index.js';
import { getLabProcessingFeeApi } from '@/api/va-order-page/chargePage.js';
import { applyConditionalDiscounts } from '@/utils/va-order-test/index.js';
import { handleException, handleBloodSalivaAddonException } from '@/utils/va-order-test/testAndPanelSelection/handleException.js';
import { handleDuplicateTests } from '@/utils/va-order-test/testAndPanelSelection/handleDuplicateTests.js';
import axios from 'axios';
import { CLOUD_SERVICE_BASE_URL } from '@/utils/const';
import ConciergeFee from '@/views/va-orderTest/checkout-page/delivery-kit/ConciergeFee.vue';
import _ from 'lodash';

const kitInventoryApi = CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === 'production'
  ? '/lis/order-inventory'
  : '/lis/order-inventory/staging'
);

export default {
  name: "CheckoutDialog",
  components: {
    NewOrderCartItems,
    PaymentMethod,
    VaOrderSummary,
    PromoCodeOrBundleDialog,
    ConciergeFee,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    roomId: {
      type: String,
      default: "",
    },
    cart: {
      type: Array,
      default: () => [],
    },
    isSubscription: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: Number,
      default: null,
    },
    customerId: {
      type: Number,
      default: null,
    },
    isClinic: {
      type: Boolean,
      default: false,
    },
    isUsCustomer: {
      type: Boolean,
      default: true,
    },
    clinicId: {
      type: Number,
      default: null,
    },
    selectedProviderId: {
      type: String,
      default: "",
    },
    useCoupon: {
      type: Boolean,
      default: false,
    },
    configMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'back', 'send'],
  data() {
    return {
      handling: {
        bloodKit: 'shipToPatient',
        nonBloodKit: 'shipToPatient',
        practiceProduct: "by_provider",
        payment: "office_card_on_file",
      },
      emailInput: '',
      phoneInput: '',
      showPracticeWarning: true,
      showContactError: false,
      bloodKitOptions: [
        { label: 'Ship to patient', value: 'shipToPatient' },
        { label: 'Supplied by Client/Office/Phlebotomist', value: 'suppliedByClient' },
      ],
      nonBloodKitOptions: [
        { label: 'Ship to patient', value: 'shipToPatient' },
        { label: 'Supplied by Client/Office/Phlebotomist', value: 'suppliedByClient' },
      ],
      isFeeLoading: false,
      isPlacingOrder: false,
      feeRefreshToken: 0,
      isOrderSummaryExpanded: false,
      // Promo code state
      promoCode: '',
      isPromoCodeValid: false,
      isShowPromoCodeValidationMessage: false,
      applyCouponLoading: false,
      invalidCouponReason: '',
      promoDiscount: 0,
      bVibrantUsed: true,
      couponExceedsVibrantSubtotal: false,
      verifyCouponPayload: null,
      verifyCouponResp: null,
      isPromoCodeOrBundleDialogVisible: false,
      pricesAfterCoupon: { withBundleBreakdown: 0, withoutBundleBreakdown: 0 },
      showConciergeFee: false,
      conciergeFeeInfo: null,
    };
  },

  computed: {
    ...mapState(['currency']),
    ...mapState('orderTest', ['countryOptions']),
    ...mapState('orderTest/user', ['patientCountry']),
    ...mapState('orderTest/order', ['deliveryMethod', 'shippingFee', 'contactInfo', 'paymentMethod', 'isUseTax', 'taxId']),
    ...mapState('orderTest/promotions', ['removeWaiveConciergeFeeCount']),
    ...mapState('orderTest/orderDraft', ['editOrderItemList']),
    ...mapGetters('orderTest/order', ['priceDetails', 'orderedSampleTypes', 'isMarkupEligible']),
    ...mapGetters('orderTest', ['selectedItemList', 'orderTestSupportObj', 'selectedVwTestPrice', 'orderItemListForApi', 'selectedItemsPrice']),
    ...mapGetters('orderTest/favorites', ['itemsInSelectedShortcuts']),
    ...mapGetters('orderTest/merchandise', ['selectedMerchList']),
    ...mapGetters('myPatients/patientInfo', ['getContactCardData']),
    dialogVisible: {
      get() {
        return this.visible;
      },
      set() {
        // Do nothing here - dialog close is handled by closeDialog method
        // This prevents auto-close when switching between dialogs
      },
    },
    subtotal() {
      return this.cart.reduce((sum, i) => sum + (i.quantity || 1) * i.price + (i.revenue || 0), 0);
    },
    hasMerchandise() {
      if (this.handling.payment === "customerPay") {
        return false;
      }
      return this.cart.some((i) => i.tab === 'practice');
    },
    hasPracticeProduct() {
      return this.cart.some((i) => i.tab === 'practice');
    },
    isPatientPaymentSelected() {
      return ['patient_pay_now', 'patient_pay_later'].includes(this.handling.payment);
    },
    hasPracticeButNoContact() {
      return this.hasPracticeProduct && this.isPatientPaymentSelected && !this.emailInput.trim() && !this.phoneInput.trim();
    },
    patientPayNoContact() {
      return this.isPatientPaymentSelected && !this.emailInput.trim() && !this.phoneInput.trim();
    },
    checkoutLineItems() {
      return this.buildLineItems();
    },
    chargeAmountForPayment() {
      return this.priceDetails.finalPrice;
    },
    orderPriceList() {
      return [this.priceDetails.finalPrice];
    },
    paymentMethodMapped() {
      const map = {
        office_card_on_file: 'customerPay',
        patient_pay_now: 'patientPayNow',
        patient_pay_later: 'patientPayLater',
      };
      return map[this.handling.payment] || 'customerPay';
    },
    hasBloodSamples() {
      return this.getSampleTypes().blood.length > 0;
    },
    hasNonBloodSamples() {
      return this.getSampleTypes().nonBlood.length > 0;
    },
    hasDryIceSamples() {
      return this.getSampleTypes().dryIce.length > 0;
    },
    kitTypeList() {
      const shipToPatientKits = [
        ...new Set(
          this.orderedSampleTypes.shipToPatient.map((sampleType) => {
            const kit = processBloodOrNonBloodKit(sampleType);
            return kit === 'nonBlood' ? 'non blood' : kit;
          })
        )
      ];
      const suppliedByClientKits = [
        ...new Set(
          this.orderedSampleTypes.suppliedByClient.map((sampleType) => {
            const kit = processBloodOrNonBloodKit(sampleType);
            return kit === 'nonBlood' ? 'non blood' : kit;
          })
        )
      ];

      const result = [];
      shipToPatientKits.forEach((kit) => {
        result.push({ type: kit, source: 'shipToPatient' });
      });
      suppliedByClientKits.forEach((kit) => {
        result.push({ type: kit, source: 'providedByClient' });
      });
      return result;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.$store.dispatch('orderTest/order/setConciergeFee', { amount: 0, isUse: false });
        this.showConciergeFee = false;
        this.conciergeFeeInfo = null;
        this.syncHandlingFromStore();
        this.showPracticeWarning = true;
        if (this.configMode) {
          this.handling.payment = 'patient_pay_later';
          this.emailInput = '';
          this.phoneInput = '';
        } else {
          const sessionPatient = JSON.parse(sessionStorage.getItem('patient') || '{}');
          this.emailInput = sessionPatient.email || '';
          this.phoneInput = sessionPatient.phone || '';
        }
        this.$store.dispatch('orderTest/order/setPaymentMethod', this.mapPaymentToStoreValue(this.handling.payment));
        if (!this.configMode) {
          this.checkBVibrantUsage();
        }
        this.$nextTick(() => {
          this.showContactError = this.hasPracticeProduct && this.isPatientPaymentSelected && !this.emailInput.trim() && !this.phoneInput.trim();
        });
        this.refreshFeesByDeliveryMethod();
        this.checkConciergeFee();
      }
    },
    'handling.payment'(newVal, oldVal) {
      this.$store.dispatch('orderTest/order/setPaymentMethod', this.mapPaymentToStoreValue(newVal));
      if (oldVal !== 'office_card_on_file' && newVal === 'office_card_on_file') {
        this.$store.dispatch('orderTest/merchandise/clearAllSelectedMerch');
      }
      this.showContactError = this.hasPracticeProduct && ['patient_pay_now', 'patient_pay_later'].includes(newVal) && !this.emailInput.trim() && !this.phoneInput.trim();
    },
    emailInput() {
      if (this.hasPracticeProduct && this.isPatientPaymentSelected) {
        this.showContactError = !this.emailInput.trim() && !this.phoneInput.trim();
      }
    },
    phoneInput() {
      if (this.hasPracticeProduct && this.isPatientPaymentSelected) {
        this.showContactError = !this.emailInput.trim() && !this.phoneInput.trim();
      }
    },
    'handling.bloodKit'(newVal) {
      this.handleDeliveryMethodChange('blood', newVal);
    },
    'handling.nonBloodKit'(newVal) {
      this.handleDeliveryMethodChange('nonBlood', newVal);
    },
    selectedMerchList: {
      deep: true,
      handler: _.debounce(function () {
        if (this.dialogVisible) {
          this.recalcTaxFee();
        }
      }, 500),
    },
    removeWaiveConciergeFeeCount(newVal) {
      if (newVal > 0 && this.promoCode) {
        this.removeCoupon();
      }
    },
    selectedItemList(newVal, oldVal) {
      if (!this.dialogVisible) return;
      // Re-verify coupon when items change (align with VA PromoCode.vue watcher)
      if (this.promoCode) {
        this.applyCoupon();
      }
      // Handle item removal side effects (align with VA price-summary handleItemRemoval)
      if (oldVal && newVal && newVal.length < oldVal.length) {
        this.handleItemRemoval();
      }
    },
  },
  methods: {
    mapPaymentToStoreValue(ehrPayment) {
      const map = {
        'office_card_on_file': 'customerPay',
        'patient_pay_now': 'patientPayNow',
        'patient_pay_later': 'patientPayLater',
      };
      return map[ehrPayment] || '';
    },
    syncHandlingFromStore() {
      this.handling.bloodKit = this.deliveryMethod?.blood || 'shipToPatient';
      this.handling.nonBloodKit = this.deliveryMethod?.nonBlood || 'shipToPatient';
    },
    updateEmail() {
      this.$store.dispatch('orderTest/order/setContactInfo', { method: 'email', info: this.emailInput });
    },
    updatePhone() {
      this.$store.dispatch('orderTest/order/setContactInfo', { method: 'phone', info: this.phoneInput });
    },
    getSampleTypes() {
      const result = {
        blood: new Set(),
        nonBlood: new Set(),
        dryIce: new Set(),
      };
      this.editOrderItemList.concat(this.selectedItemList).forEach((item) => {
        if (item.sampleTypeSets) {
          ['blood', 'nonBlood', 'dryIce'].forEach((prop) => {
            result[prop] = new Set([...item.sampleTypeSets[prop], ...result[prop]]);
          });
        }
      });
      return {
        blood: [...result.blood],
        nonBlood: [...result.nonBlood],
        dryIce: [...result.dryIce],
      };
    },
    async handleDeliveryMethodChange(kit, deliveryMethod) {
      if (!this.dialogVisible || !deliveryMethod) {
        return;
      }
      if ((this.deliveryMethod?.[kit] || '') === deliveryMethod) {
        return;
      }
      this.$store.dispatch('orderTest/order/setDeliveryMethod', { kit, deliveryMethod });
      await this.refreshFeesByDeliveryMethod();
    },
    async refreshFeesByDeliveryMethod() {
      const requestToken = ++this.feeRefreshToken;
      this.isFeeLoading = true;
      try {
        await this.checkShippingFee();
        if (requestToken !== this.feeRefreshToken) {
          return;
        }
        await this.recalcTaxFee();
      } finally {
        if (requestToken === this.feeRefreshToken) {
          this.isFeeLoading = false;
        }
      }
    },
    async checkShippingFee() {
      const countryFound = this.countryOptions.find(
        (country) => country.countryNameTwoBytes === this.patientCountry
      );
      const patient = JSON.parse(sessionStorage.getItem('patient') || '{}');
      const postalCode = patient && patient.zipcode ? patient.zipcode : '';
      const isUSOrCA = countryFound && (countryFound.vibrantCountryId == '238' || countryFound.vibrantCountryId == '42');

      if (!countryFound || isUSOrCA || this.kitTypeList.length === 0) {
        this.$store.dispatch('orderTest/order/setShippingFee', { importFee: 0, exportFee: 0, total: 0 });
        return;
      }

      const itemIdList = this.selectedItemList?.map((item) => item.id).filter(Boolean) || [];
      const { data } = await getInternationalShippingFee({
        countryCode: countryFound.vibrantCountryId,
        kitTypeList: this.kitTypeList,
        postalCode,
        currency: (this.currency?.name || 'usd').toLowerCase(),
        itemIdList,
      });

      const priceList = data || [];
      const importFee = priceList
        .filter((priceItem) => priceItem.fee_type === 'IMPORT')
        .reduce((acc, priceItem) => acc + Number(priceItem.price), 0);
      const exportFee = priceList
        .filter((priceItem) => priceItem.fee_type === 'EXPORT')
        .reduce((acc, priceItem) => acc + Number(priceItem.price), 0);

      this.$store.dispatch('orderTest/order/setShippingFee', {
        importFee,
        exportFee,
        total: importFee + exportFee,
      });
    },
    async recalcTaxFee() {
      const selectedTaxableTestForAPI = [];
      const { selectedItemList } = this.orderTestSupportObj;

      selectedItemList.forEach((test) => {
        if (test.taxCode) {
          selectedTaxableTestForAPI.push({
            amount: test.price.toFixed(2),
            quantity: 1,
            reference: test.id.toString(),
            tax_code: test.taxCode,
            tax_behavior: 'exclusive',
          });
        }
      });

      this.selectedMerchList.forEach((item) => {
        if (item.merch.taxCode) {
          const quantity = item.quantity;
          if (quantity === 0) {
            return;
          }
          selectedTaxableTestForAPI.push({
            amount: (item.merch.currentPrice * quantity).toFixed(2),
            quantity,
            reference: `merch_${item.id}`,
            tax_code: item.merch.taxCode,
            tax_behavior: 'exclusive',
          });
        }
      });

      const patient = JSON.parse(sessionStorage.getItem('patient') || '{}');
      const hasTaxItems = selectedTaxableTestForAPI.length > 0;
      const hasAddress = patient.country && patient.state && patient.zipcode && patient.city && patient.streetAddress;

      if (!hasTaxItems || !hasAddress) {
        this.$store.dispatch('orderTest/order/setTaxFee', { taxFee: 0, isUseTax: false, taxId: '' });
        return;
      }

      try {
        const res = await calculateTax({
          line_items: selectedTaxableTestForAPI,
          customer_details: {
            countryName: patient.country,
            customerState: patient.state,
            customerZipcode: patient.zipcode,
            customerCity: patient.city,
            customerStreet: patient.streetAddress,
            address_type: 'shipping',
          },
          shipping_fee: this.shippingFee.total,
        });
        this.$store.dispatch('orderTest/order/setTaxFee', {
          taxFee: res.data.tax_amount,
          isUseTax: true,
          taxId: res.data.id,
        });
      } catch (error) {
        this.$store.dispatch('orderTest/order/setTaxFee', { taxFee: 0, isUseTax: false, taxId: '' });
      }
    },
    async recalcTaxFeeAfterCoupon(priceDetail) {
      try {
        const selectedTaxableTestForAPI = [];
        const { selectedItemList } = this.orderTestSupportObj;

        const taxableTestIds = new Map();
        selectedItemList.forEach((test) => {
          if (test.taxCode) {
            taxableTestIds.set(test.id, test.taxCode);
          }
        });

        priceDetail.forEach((test) => {
          const selectedTestId = Number(test.itemId);
          if (taxableTestIds.has(selectedTestId)) {
            selectedTaxableTestForAPI.push({
              amount: test.sellingPrice.toFixed(2),
              quantity: 1,
              reference: test.itemId.toString(),
              tax_code: taxableTestIds.get(selectedTestId),
              tax_behavior: 'exclusive',
            });
          }
        });

        this.selectedMerchList.forEach((item) => {
          if (item.merch.taxCode) {
            const quantity = item.quantity;
            if (quantity === 0) return;
            selectedTaxableTestForAPI.push({
              amount: (item.merch.currentPrice * quantity).toFixed(2),
              quantity,
              reference: `merch_${item.id}`,
              tax_code: item.merch.taxCode,
              tax_behavior: 'exclusive',
            });
          }
        });

        if (selectedTaxableTestForAPI.length > 0) {
          const patient = JSON.parse(sessionStorage.getItem('patient') || '{}');
          const res = await calculateTax({
            line_items: selectedTaxableTestForAPI,
            customer_details: {
              countryName: patient.country,
              customerState: patient.state,
              customerZipcode: patient.zipcode,
              customerCity: patient.city,
              customerStreet: patient.streetAddress,
              address_type: 'shipping',
            },
            shipping_fee: this.shippingFee.total,
          });
          this.$store.dispatch('orderTest/order/setTaxFee', {
            taxFee: res.data.tax_amount,
            isUseTax: true,
            taxId: res.data.id,
          });
        }
      } catch (error) {
        console.error('Failed to calculate tax after coupon:', error);
      }
    },
    recalcLabProcessingFee() {
      if (this.selectedItemList.length === 0) {
        this.$store.dispatch('orderTest/order/setLabProcessingFee', 0);
        return;
      }
      const { orderTestsPrice } = this.priceDetails;
      const price = orderTestsPrice < this.promoDiscount
        ? 0
        : orderTestsPrice - this.promoDiscount;
      const patient = JSON.parse(sessionStorage.getItem('patient') || '{}');
      getLabProcessingFeeApi({
        orderItems: this.orderItemListForApi.filter(item => item.item_type !== 'merchandise'),
        original_price: price.toFixed(2),
        patientId: patient?.id ? Number(patient.id) : 0,
        useCoupon: this.promoDiscount > 0,
      }).then(res => {
        this.$store.dispatch('orderTest/order/setLabProcessingFee', res.data);
      }).catch(err => {
        console.error('Failed to recalc lab processing fee:', err);
      });
    },
    handleItemRemoval() {
      const itemList = this.selectedItemList
        .filter(i => i.category !== 'shortcut')
        .concat(this.itemsInSelectedShortcuts || []);
      const { testList } = this.orderTestSupportObj;
      applyConditionalDiscounts(testList, 'isSelect', itemList);
      handleException('isSelect', 'isNotAllowedSelect', this.orderTestSupportObj);
      handleBloodSalivaAddonException('isSelect', 'isNotAllowedSelect', this.orderTestSupportObj);
      handleDuplicateTests('isSelect', this.orderTestSupportObj);
      this.recalcLabProcessingFee();
      this.recalcTaxFee();
    },
    toggleOrderSummaryExpand() {
      this.isOrderSummaryExpanded = !this.isOrderSummaryExpanded;
    },
    closeDialog() {
      this.resetState();
      this.$emit("close");
    },
    resetState() {
      this.handling = {
        bloodKit: 'shipToPatient',
        nonBloodKit: 'shipToPatient',
        practiceProduct: "by_provider",
        payment: "office_card_on_file",
      };
      this.emailInput = '';
      this.phoneInput = '';
      this.isFeeLoading = false;
      this.feeRefreshToken += 1;
      // Clean up coupon and restore item list structure (align with VA PromoCode.vue unmounted)
      this.removeCoupon();
      this.showConciergeFee = false;
      this.conciergeFeeInfo = null;
      this.$store.dispatch('orderTest/order/setConciergeFee', { amount: 0, isUse: false });
    },
    goBack() {
      this.$emit("back");
    },
    validateEmail(value) {
      if (!value.trim()) return true;
      const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;
      return regex.test(value.trim());
    },
    validateBeforePlaceOrder() {
      if (this.emailInput.trim() && !this.validateEmail(this.emailInput)) {
        notify({
          type: 'error',
          title: 'Invalid Email',
          message: 'Please enter a valid email address.',
          duration: 5000,
          showClose: false,
        });
        return false;
      }
      if (this.hasPracticeProduct && this.isPatientPaymentSelected && !this.emailInput.trim() && !this.phoneInput.trim()) {
        notify({
          type: 'error',
          title: 'Unable to checkout',
          message: 'Practice products require patient email or phone number for order notification.',
          duration: 5000,
          showClose: false,
        });
        return false;
      }
      return true;
    },
    handlePlaceOrderClick() {
      // For patientPayLater, place order directly (no payment collection needed)
      if (this.paymentMethodMapped === 'patientPayLater') {
        this.onCompletePatientPayLater();
      }
      // For other payment methods, the PaymentMethod component handles payment
      // and will emit completePatientPayNow / completeCustomerPay
    },
    onCompletePatientPayNow(paymentInfo) {
      if (this.hasPracticeProduct && !this.emailInput.trim() && !this.phoneInput.trim()) {
        this.showContactError = true;
        return;
      }
      this.placeOrder(paymentInfo);
    },
    onCompleteCustomerPay(paymentInfo) {
      this.placeOrder(paymentInfo);
    },
    async onCompletePatientPayLater() {
      if (this.hasPracticeProduct && !this.emailInput.trim() && !this.phoneInput.trim()) {
        this.showContactError = true;
        return;
      }
      const orderSamplePriceMaps = await getOrderSamplePriceMaps(this.orderPriceList);
      this.placeOrder({ orderSamplePriceMaps });
    },
    async saveConfigOrder() {
      try {
        const payload = await this.buildConfigOrderPayload();
        this.$emit("send", {
          success: true,
          payload,
          items: this.cart,
          chargeMethod: 'patientPayLater',
          orderItems: this.orderItemListForApi || [],
          isDbsVersion: this.$store.state.orderTest.isDbsVersion,
        });
        this.$store.dispatch('orderTest/clearSelectedItemList');
        this.$store.dispatch('orderTest/merchandise/clearAllSelectedMerch');
        this.resetState();
        this.$emit("close");
      } catch (error) {
        console.error("[CheckoutDialog] saveConfigOrder failed:", error);
      }
    },
    async buildConfigOrderPayload() {
      const customerId = this.selectedProviderId ? Number(this.selectedProviderId) : null;

      const line_items = this.checkoutLineItems;

      const deliver_method = {};
      if (this.hasBloodSamples) deliver_method.blood = this.handling.bloodKit;
      if (this.hasNonBloodSamples) deliver_method.non_blood = this.handling.nonBloodKit;

      const notification = {
        send_email: true,
        send_text: true,
        send_date: "",
        email: this.emailInput.trim(),
        phone: this.phoneInput.trim(),
      };

      // Fees - same logic as buildPlaceOrderPayload
      const fees = [];
      const { labProcessingFee, conciergeFee, consultationFee, consultationFeeName } = this.priceDetails;
      const { importFee, exportFee } = this.shippingFee;
      if (labProcessingFee > 0) fees.push({ type: "labProcessingFee", amount: parseFloat(labProcessingFee.toFixed(2)), notes: "" });
      if (importFee > 0) fees.push({ type: "importFee", amount: parseFloat(importFee.toFixed(2)), notes: "" });
      if (exportFee > 0) fees.push({ type: "exportFee", amount: parseFloat(exportFee.toFixed(2)), notes: "" });
      if (conciergeFee > 0) fees.push({ type: "conciergeFee", amount: parseFloat(conciergeFee.toFixed(2)), notes: "" });
      if (consultationFee > 0) fees.push({ type: "consultationFee", amount: parseFloat(consultationFee.toFixed(2)), notes: consultationFeeName || "" });

      return {
        line_items,
        deliver_method,
        customer_id: customerId,
        patient_id: null,
        clinic_id: Number((this.$store.state.orderTest.user.userInfo || {}).clinic_id) || null,
        sample_id: null,
        accession_id: null,
        charge_method: 'patientPayLater',
        payments: [],
        currency: (this.currency?.name || 'usd').toLowerCase(),
        source: "",
        total_charge: this.priceDetails.finalPrice ?? 0,
        note_to_patient: "",
        discounts: [],
        notification,
        fees,
        tax: {
          use_tax: this.isUseTax || false,
          tax_id: this.taxId || "",
          tax_fee: parseFloat((this.priceDetails.taxFee || 0).toFixed(2)),
        },
        draft_id: null,
        tube_info: [],
        dbs: this.$store.state.orderTest.isDbsVersion || false,
      };
    },
    async placeOrder(paymentInfo) {
      if (!this.validateBeforePlaceOrder()) {
        return;
      }
      // VP-18765: a saved patient address over the shipping length limit cannot
      // ship, so the order is blocked and the toast names the field to fix on
      // the patient's record.
      const sessionPatient = JSON.parse(sessionStorage.getItem('patient') || '{}');
      const lengthErrors = getShippingAddressLengthErrors(sessionPatient);
      const overLimitField = Object.keys(lengthErrors)[0];
      if (overLimitField) {
        notify({
          type: 'error',
          title: 'Error',
          message: formatShippingLengthMessage(SHIPPING_FIELD_LABELS[overLimitField], lengthErrors[overLimitField]),
        });
        return;
      }
      this.isPlacingOrder = true;
      try {
        const payload = await this.buildPlaceOrderPayload(paymentInfo);
        console.log("Place order V2 payload:", JSON.stringify(payload, null, 2));
        const { data } = await placeOrderV2(payload);

        // Update kit inventory after successful order (same as VA Portal)
        await this.updateInventory(data.order_id);

        this.$emit("send", {
          success: true,
          orderId: data.order_id,
          sampleId: data.sample_id,
          accessionId: data.accession_id,
          items: this.cart,
          amount: this.priceDetails.finalPrice ?? 0,
          chargeMethod: this.paymentMethodMapped || '',
        });
        notify({
          type: 'success',
          title: 'Order Placed',
          message: 'Your order has been placed successfully.',
          duration: 3000,
          showClose: false,
        });
        
        // Clear Vuex store cart data to reset subtotal
        this.$store.dispatch('orderTest/clearSelectedItemList');
        this.$store.dispatch('orderTest/merchandise/clearAllSelectedMerch');
        this.$store.dispatch('orderTest/program/clearSelectedPrograms');
        
        this.resetState();
        this.$emit("close");
      } catch (error) {
        console.error("Place order failed:", error);
        notify({
          type: 'error',
          title: 'Order Failed',
          message: error?.response?.data?.message || error?.message || 'Failed to place order. Please try again.',
          duration: 5000,
          showClose: true,
        });
      } finally {
        this.isPlacingOrder = false;
      }
    },
    async updateInventory(orderId) {
      const userInfo = this.$store.state.orderTest.user.userInfo;
      if (!userInfo || _.isEmpty(userInfo.beta_programs) || !userInfo.beta_programs.includes('general_beta1')) return;
      const inventoryUpdatedQuantity = this.$store.state.orderTest.order.inventoryUpdatedQuantity || {};
      if (Object.keys(inventoryUpdatedQuantity).length === 0 || this.deliveryMethod?.nonBlood !== 'suppliedByClient') return;
      try {
        const items = Object.entries(inventoryUpdatedQuantity).map(([key, quantity]) => ({
          item_id: Number(key),
          quantity,
        }));
        const token = sessionStorage.getItem('jwtToken');
        await axios.post(`${kitInventoryApi}/kit-inventory`, {
          account_type: 'customer',
          account_id: userInfo.customer_id,
          clinic_id: userInfo.clinic_id || null,
          type: 'usage',
          source: 'testorder',
          source_id: orderId,
          notes: '',
          items,
        }, { headers: { Authorization: 'Bearer ' + token } });
      } catch (err) {
        console.error('Failed to update inventory:', err);
      }
    },
    async checkConciergeFee() {
      try {
        // Only applicable if order has blood samples
        const hasBlood = this.selectedItemList?.some(item => item.sampleTypeSets?.blood?.size > 0);
        if (!hasBlood) {
          this.showConciergeFee = false;
          this.conciergeFeeInfo = null;
          return;
        }
        const patient = JSON.parse(sessionStorage.getItem('patient') || '{}');
        if (!patient.streetAddress || !patient.city || !patient.state || !patient.zipcode) {
          this.showConciergeFee = false;
          this.conciergeFeeInfo = null;
          return;
        }
        const address = [patient.streetAddress, patient.city, patient.zipcode, patient.state, patient.country].filter(Boolean).join(', ');
        const orderPrice = this.priceDetails?.priceBeforeDiscount || 0;
        const { data } = await isValidConciergeFeeV2Api({ address, order_price: orderPrice.toString() });
        if (data?.is_valid_concierge && data.concierge_final_price > 0) {
          this.conciergeFeeInfo = data;
          this.showConciergeFee = true;
        } else {
          this.showConciergeFee = false;
          this.conciergeFeeInfo = null;
        }
      } catch (err) {
        console.error('Failed to check concierge fee:', err);
        this.showConciergeFee = false;
        this.conciergeFeeInfo = null;
      }
    },
    async checkBVibrantUsage() {
      try {
        const res = await preCheckCouponUsageApi();
        this.bVibrantUsed = !!res?.data?.bVibrantUsed;
      } catch (e) {
        this.bVibrantUsed = true;
        console.error('preCheckCouponUsageApi error', e);
      }
    },
    async applyCoupon() {
      if (!this.promoCode) {
        this.isPromoCodeValid = false;
        this.isShowPromoCodeValidationMessage = true;
        this.invalidCouponReason = 'The promo code you entered is invalid.';
        return;
      }
      this.applyCouponLoading = true;
      try {
        const currency = this.currency?.name?.toLowerCase() || 'usd';
        const patient = JSON.parse(sessionStorage.getItem('patient') || '{}');
        const patientId = patient?.id ? Number(patient.id) : null;
        const payload = getVerifyCouponApiPayload(
          null, currency, this.selectedItemList, this.promoCode,
          this.orderTestSupportObj, patientId
        );
        this.verifyCouponPayload = payload;
        const res = await verifyCouponCodeApi(payload);
        const { totalDeduction, discountType, priceDetail, discountValue } = res.data;
        let promoDiscount = Number(totalDeduction);

        if (promoDiscount > 0) {
          if (discountType === 'waive_concierge_fee') {
            this.$store.commit('orderTest/promotions/SET_WAIVE_CONCIERGE_FEE', true);
          }
          // If bundles need breakdown, show choice dialog (align with VA Portal)
          const needBreakdown = priceDetail.some(item => item.bundleId);
          if (needBreakdown) {
            const selectedItemsPrice = this.selectedItemList.reduce((acc, item) => acc + item.price, 0);
            const { priceWithBreakdown, priceWithoutBreakdown } = getPricesAfterCouponApplied(res.data, selectedItemsPrice, payload.unlock_deals);
            this.pricesAfterCoupon = { withBundleBreakdown: priceWithBreakdown, withoutBundleBreakdown: priceWithoutBreakdown };
            this.verifyCouponResp = res.data;
            this.isPromoCodeOrBundleDialogVisible = true;
            return;
          }
          await this.recalcTaxFeeAfterCoupon(priceDetail);
          this.promoDiscount = Number(promoDiscount.toFixed(2));
          this.isPromoCodeValid = true;
          this.$store.dispatch('orderTest/order/setPromoCode', this.promoCode);
          this.$store.dispatch('orderTest/order/setPromoDiscount', this.promoDiscount);
          if (discountType === 'absolute_off' && discountValue > totalDeduction) {
            this.couponExceedsVibrantSubtotal = true;
          }
        } else {
          this.isPromoCodeValid = false;
          this.invalidCouponReason = 'The promo code you entered is invalid.';
        }
        this.isShowPromoCodeValidationMessage = true;
      } catch (error) {
        this.isPromoCodeValid = false;
        this.invalidCouponReason = error.response?.data?.description || 'The promo code you entered is invalid.';
        this.promoCode = '';
        this.$store.dispatch('orderTest/order/setPromoCode', '');
        this.$store.dispatch('orderTest/order/setPromoDiscount', 0);
        this.isShowPromoCodeValidationMessage = true;
      } finally {
        this.applyCouponLoading = false;
      }
    },
    async userChoosesBundle() {
      this.isPromoCodeOrBundleDialogVisible = false;
      try {
        const { discountType, discountValue, totalDeduction, priceDetail } = this.verifyCouponResp;
        // Apply coupon only to single tests (keep bundles intact)
        const singleTestsWithCoupon = priceDetail.filter(item => !item.bundleId);
        if (singleTestsWithCoupon.length > 0) {
          const promoDiscount = Number(singleTestsWithCoupon.reduce((acc, item) => acc + (item.originalPrice - item.sellingPrice), 0).toFixed(2));
          await this.recalcTaxFeeAfterCoupon(priceDetail);
          this.$store.dispatch('orderTest/order/setPromoCode', this.promoCode);
          this.$store.dispatch('orderTest/order/setPromoDiscount', promoDiscount);
          this.promoDiscount = promoDiscount;
          if (discountType === 'absolute_off' && discountValue > totalDeduction) {
            this.couponExceedsVibrantSubtotal = true;
          }
          this.isPromoCodeValid = true;
          this.isShowPromoCodeValidationMessage = true;
        } else {
          this.initializeCouponState();
        }
      } catch (error) {
        console.error('userChoosesBundle error:', error);
        this.initializeCouponState();
      } finally {
        this.applyCouponLoading = false;
      }
    },
    async userChoosesPromoCode() {
      this.isPromoCodeOrBundleDialogVisible = false;
      try {
        const { discountType, discountValue, totalDeduction, priceDetail } = this.verifyCouponResp;
        const unlockDeals = this.verifyCouponPayload.unlock_deals;
        breakdownCouponEligibleBundles(priceDetail, unlockDeals, this.orderTestSupportObj);
        await this.recalcTaxFeeAfterCoupon(priceDetail);
        const promoDiscount = Number(totalDeduction);
        this.$store.dispatch('orderTest/order/setPromoCode', this.promoCode);
        this.$store.dispatch('orderTest/order/setPromoDiscount', promoDiscount);
        this.promoDiscount = promoDiscount;
        if (discountType === 'absolute_off' && discountValue > totalDeduction) {
          this.couponExceedsVibrantSubtotal = true;
        }
        this.isPromoCodeValid = true;
        this.isShowPromoCodeValidationMessage = true;
      } catch (error) {
        console.error('userChoosesPromoCode error:', error);
        this.initializeCouponState();
      } finally {
        this.applyCouponLoading = false;
      }
    },
    closePromoCodeOrBundleDialog() {
      this.isPromoCodeOrBundleDialogVisible = false;
      this.initializeCouponState();
    },
    initializeCouponState() {
      this.promoCode = '';
      this.isPromoCodeValid = false;
      this.isShowPromoCodeValidationMessage = false;
      this.applyCouponLoading = false;
      this.invalidCouponReason = '';
      this.promoDiscount = 0;
      this.couponExceedsVibrantSubtotal = false;
      this.verifyCouponPayload = null;
      this.verifyCouponResp = null;
      this.$store.dispatch('orderTest/order/setPromoCode', '');
      this.$store.dispatch('orderTest/order/setPromoDiscount', 0);
    },
    removeCoupon() {
      // Restore shortcuts and reform bundles (align with VA Portal)
      const selectedShortcuts = this.selectedItemList.filter(item => item.category === 'shortcut');
      const unlockDeals = this.verifyCouponPayload?.unlock_deals;
      if (unlockDeals) {
        handleShortcutAfterCouponRemoved(selectedShortcuts, unlockDeals);
      }
      const bestDealIneligiblePrice = this.selectedItemList
        .filter(item => ['shortcut', 'customPanel'].includes(item.category))
        .reduce((acc, item) => acc + item.price, 0);
      const selectedItemsPrice = this.selectedItemList.reduce((acc, item) => acc + item.price, 0);
      const bestDealApplicablePrice = selectedItemsPrice - bestDealIneligiblePrice;
      const discountMap = this.$store.state.orderTest.discountMap;
      const isDbsVersion = this.$store.state.orderTest.isDbsVersion;
      handleBestDealSuggestion('isSelect', bestDealApplicablePrice, this.orderTestSupportObj, discountMap, isDbsVersion);

      this.promoCode = '';
      this.isPromoCodeValid = false;
      this.isShowPromoCodeValidationMessage = false;
      this.applyCouponLoading = false;
      this.invalidCouponReason = '';
      this.promoDiscount = 0;
      this.couponExceedsVibrantSubtotal = false;
      this.verifyCouponPayload = null;
      this.$store.dispatch('orderTest/order/setPromoCode', '');
      this.$store.dispatch('orderTest/order/setPromoDiscount', 0);
      this.$store.commit('orderTest/promotions/SET_WAIVE_CONCIERGE_FEE', false);
      this.recalcTaxFee();
    },
    applyBVibrantCoupon() {
      this.promoCode = 'bVibrant';
      this.$nextTick(() => {
        this.applyCoupon();
      });
    },
    getPrimaryAccessionId(paymentInfo, sampleId = this.getPrimarySampleId(paymentInfo)) {
      if (paymentInfo?.julienBarcode) {
        return String(paymentInfo.julienBarcode);
      }
      const sampleIdMap = paymentInfo?.orderSamplePriceMaps?.sampleIdJulienBarcodeMap || {};
      return sampleId ? String(sampleIdMap[sampleId] || '') : '';
    },
    getPrimarySampleId(paymentInfo) {
      if (paymentInfo?.sampleId) {
        return Number(paymentInfo.sampleId);
      }
      const sampleIdMap = paymentInfo?.orderSamplePriceMaps?.sampleIdJulienBarcodeMap || {};
      const [firstSampleId] = Object.keys(sampleIdMap);
      return firstSampleId ? Number(firstSampleId) : 0;
    },
    async getTubeInfoPayload() {
      if (!this.orderItemListForApi || this.orderItemListForApi.length === 0) {
        return [];
      }
      try {
        const { data } = await createTubeInfo(this.orderItemListForApi);
        const tubeMap = data?.noOfTubes || {};
        return Object.entries(tubeMap).map(([tubeType, tubeCount]) => ({
          tube_type: tubeType,
          tube_count: Number(tubeCount),
        }));
      } catch (error) {
        console.log('Failed to build tube_info:', error);
        return [];
      }
    },
    async buildPlaceOrderPayload(paymentInfo) {
      const userInfo = this.$store.state.orderTest.user.userInfo || {};
      const customerId = this.selectedProviderId
        ? Number(this.selectedProviderId)
        : (this.customerId || Number(userInfo.customer_id) || null);
      const clinicId = this.clinicId || Number(userInfo.clinic_id) || null;

      const line_items = this.checkoutLineItems;
      const finalCharge = this.priceDetails.finalPrice;
      const primarySampleId = this.getPrimarySampleId(paymentInfo);
      const tube_info = await this.getTubeInfoPayload();

      // Delivery method
      const deliver_method = {};
      if (this.hasBloodSamples) deliver_method.blood = this.handling.bloodKit;
      if (this.hasNonBloodSamples) deliver_method.non_blood = this.handling.nonBloodKit;

      // Payments
      const payments = [];
      if (paymentInfo?.paymentId) {
        const paymentType = paymentInfo.type || (paymentInfo.isAchPayment ? 'ach' : 'card');
        const paymentPlatform = paymentInfo.platform || 'stax';
        payments.push({
          id: String(paymentInfo.paymentId),
          payment_type: paymentType,
          payment_platform: paymentPlatform,
          amount: finalCharge,
        });
      }
      if (paymentInfo?.usedCredit > 0) {
        payments.push({
          id: null,
          payment_type: "credit",
          payment_platform: null,
          amount: parseFloat(paymentInfo.usedCredit.toFixed(2)),
        });
      }

      // Fees
      const fees = [];
      const { labProcessingFee, conciergeFee, consultationFee, consultationFeeName } = this.priceDetails;
      const { importFee, exportFee } = this.shippingFee;
      if (labProcessingFee > 0) fees.push({ type: "labProcessingFee", amount: parseFloat(labProcessingFee.toFixed(2)), notes: "" });
      if (importFee > 0) fees.push({ type: "importFee", amount: parseFloat(importFee.toFixed(2)), notes: "" });
      if (exportFee > 0) fees.push({ type: "exportFee", amount: parseFloat(exportFee.toFixed(2)), notes: "" });
      if (conciergeFee > 0) fees.push({ type: "conciergeFee", amount: parseFloat(conciergeFee.toFixed(2)), notes: "" });
      if (consultationFee > 0) fees.push({ type: "consultationFee", amount: parseFloat(consultationFee.toFixed(2)), notes: consultationFeeName || "" });

      // Notification
      const notification = {
        send_email: !!this.emailInput.trim(),
        send_text: !!this.phoneInput.trim(),
        send_date: "",
        email: this.emailInput.trim(),
        phone: this.phoneInput.trim(),
      };

      // Tax
      const tax = {
        use_tax: this.isUseTax || false,
        tax_id: this.taxId || "",
        tax_fee: parseFloat((this.priceDetails.taxFee || 0).toFixed(2)),
      };

      return {
        line_items,
        deliver_method,
        customer_id: customerId,
        patient_id: this.patientId,
        clinic_id: clinicId,
        sample_id: primarySampleId,
        accession_id: Number(this.getPrimaryAccessionId(paymentInfo, primarySampleId)) || null,
        charge_method: this.paymentMethodMapped,
        payments,
        currency: (this.currency?.name || 'usd').toLowerCase(),
        source: "",
        total_charge: finalCharge,
        note_to_patient: "",
        discounts: this.promoDiscount > 0 ? [{
          discount_type: 'coupon',
          description: this.promoCode,
          discount_amount: parseFloat(this.promoDiscount.toFixed(2)),
        }] : [],
        notification,
        fees,
        tax,
        draft_id: null,
        tube_info,
        dbs: this.$store.state.orderTest.isDbsVersion || false,
      };
    },
    buildLineItems() {
      const data = {
        editOrderItemList: this.editOrderItemList || [],
        paymentMethod: this.paymentMethod,
        isMarkupEligible: this.isMarkupEligible,
        selectedItemList: this.selectedItemList || [],
        selectedMerchList: this.selectedMerchList || [],
        selectedVwTestPrice: this.selectedVwTestPrice || 0,
        testNodeList: this.orderTestSupportObj?.testNodeList || [],
        bundleList: this.orderTestSupportObj?.bundleList || [],
      };
      return getLineItemsPayalod(data);
    },
  },
};
</script>

<style>
.checkout-dialog .el-overlay-dialog {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px;
}

.checkout-dialog .el-dialog {
  border-radius: 10px;
  margin: 0 auto;
  --el-dialog-padding-primary: 0px !important;
  font-family: 'Inter', sans-serif;
}

.checkout-dialog .el-dialog {
  max-height: calc(100vh - 40px) !important;
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.checkout-dialog .el-dialog__header {
  padding: 0;
  border-bottom: none;
}

.checkout-dialog .el-dialog__body {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.checkout-dialog.el-dialog__header {
  padding: 0;
  margin: 0;
}

.checkout-dialog .el-dialog__body {
  padding: 0;
}

.checkout-dialog .el-dialog__footer {
  padding: 0;
}

.checkout-dialog {
  border-radius: 12px;
  overflow: hidden;
}
</style>

<style scoped>
:deep(.el-select__wrapper) {
  box-shadow: none !important;
}

.checkout-kit-selector {
  margin-top: 4px;
}

.checkout-kit-selector-wide {
  width: 360px;
  max-width: 100%;
  flex: 0 0 360px;
}

.checkout-kit-selector :deep(.new-libary-single-selector .el-select__wrapper) {
  min-height: 34px;
  border-radius: 4px;
  border: 1px solid #c4c4c4;
  background: #FFF;
  padding: 0 10px;
}

.checkout-kit-selector :deep(.new-libary-single-selector .el-select__placeholder),
.checkout-kit-selector :deep(.new-libary-single-selector .el-select__selected-item) {
  font-size: 12px;
  color: #5f6773;
  line-height: 18px;
}

.checkout-kit-selector :deep(.new-libary-single-selector .el-select__caret) {
  color: #8b93a0;
  font-size: 12px;
}

.checkout-kit-selector :deep(.new-libary-single-selector .el-select__wrapper.is-focused) {
  border-color: #7f9bb3;
  box-shadow: none !important;
}

:deep(.el-radio__inner) {
  width: 20px;
  height: 20px;
  border: 2px solid #004879;
  background-color: white;
}

:deep(.el-radio__input.is-checked .el-radio__inner) {
  background-color: white;
  border-color: #004879;
}

:deep(.el-radio__input.is-checked .el-radio__inner::after) {
  width: 12px;
  height: 12px;
  background-color: #004879;
  transform: translate(-50%, -50%) scale(1);
}

:deep(.el-radio__inner:hover) {
  border-color: #004879;
}

:deep(.el-radio__input.is-focus .el-radio__inner) {
  box-shadow: none;
}

.order-stage {
  position: relative;
}

.cart-summary-container {
  border-radius: 5px;
  background: var(--Cool-White, #f9fbfd);
  box-shadow: 0 2px 12px 0 rgba(74, 74, 74, 0.15);
  padding: 16px;
  width: 100%;
}

.step-two-container {
  border-radius: 5px;
  background: var(--Cool-White, #f9fbfd);
  /* box-shadow: 0 2px 12px 0 rgba(74, 74, 74, 0.15); */
  padding: 24px;
  width: 100%;
  height: auto;
}

.step-section {
  padding: 0;
}

.step-two-form-wrapper {
  border-radius: 10px;
  border: 1px dashed var(--DividerGray, #C4C4C4);
  background: #F9F9F9;
  padding: 24px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #c8d2de transparent;
}

.step-two-form-wrapper::-webkit-scrollbar {
  width: 4px;
  background: transparent;
}

.step-two-form-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.step-two-form-wrapper::-webkit-scrollbar-thumb {
  background: #c8d2de;
  border-radius: 999px;
}

.step-two-form-wrapper::-webkit-scrollbar-thumb:hover {
  background: #aebcca;
}


.step-title {
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.label {
  width: 170px;
  min-width: 170px;
  display: inline-block;
  color: #4a4a4a;
}

/* Two Column Layout */
.dialog-content-wrapper {
  padding: 24px;
  background: #f9fbfd;
  color: #333;
  min-height: 500px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', sans-serif;
  border-radius: 0 0 10px 10px;
  position: relative;
}

.two-column-layout {
  display: flex;
  flex-direction: row;
  position: relative;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  gap: 16px;
}

.left-column {
  flex: 1;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.right-column {
  width: 286px;
  min-width: 286px;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
}

.order-summary-sidebar {
  width: 286px;
  min-width: 286px;
  height: 100%;
  min-height: 0;
  padding: 0;
  overflow: visible;
  position: relative;
}

.order-summary-sidebar.order-summary-expanded {
  z-index: 60;
}

.order-summary-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 286px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: #FFF;
  box-shadow: -3px -1px 6px 0 rgba(74, 74, 74, 0.14);
  overflow: hidden;
  transition: width 220ms ease-in-out, box-shadow 220ms ease-in-out;
  font-family: 'Inter', sans-serif;
}

.order-summary-panel :deep(*) {
  font-family: 'Inter', sans-serif;
}

.order-summary-sidebar.order-summary-expanded .order-summary-panel {
  width: 420px;
}

.order-summary-embedded {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.order-summary-sidebar :deep(.order-summary-title) {
  padding-left: 36px;
}

.order-summary-sidebar :deep(.order-summary-container) {
  height: 100%;
  overflow-y: auto;
  padding: 16px 16px 120px !important;
  scrollbar-width: thin;
  scrollbar-color: #c8d2de transparent;
}

.order-summary-sidebar :deep(.order-summary-container)::-webkit-scrollbar {
  width: 4px;
  background: transparent;
}

.order-summary-sidebar :deep(.order-summary-container)::-webkit-scrollbar-track {
  background: transparent;
}

.order-summary-sidebar :deep(.order-summary-container)::-webkit-scrollbar-thumb {
  background: #c8d2de;
  border-radius: 999px;
}

.order-summary-sidebar :deep(.order-summary-container)::-webkit-scrollbar-thumb:hover {
  background: #aebcca;
}

@media (max-width: 1024px) {
  .two-column-layout {
    flex-direction: column;
    padding-right: 0;
  }
  .right-column {
    position: static;
    top: auto;
    right: auto;
    bottom: auto;
    width: 100%;
  }
}

.checkout-kit-selector :deep(.new-library-input .el-input__wrapper) {
  min-height: 34px;
  height: 34px;
  border-radius: 4px;
  border: 1px solid #c4c4c4;
  background: #FFF;
  padding: 0 10px;
  box-shadow: none;
}

.checkout-kit-selector :deep(.new-library-input .el-input__wrapper .el-input__inner) {
  font-size: 14px;
  color: #333;
  line-height: 20px;
  height: 24px;
  background: #FFF;
}

.checkout-kit-selector :deep(.new-library-input .el-input__wrapper .el-input__inner::placeholder) {
  font-size: 14px;
  color: #a8a8a8;
}

.back-to-shop-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background: white;
  border: 1px solid #004879;
  border-radius: 4px;
  color: #004879;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-to-shop-btn:hover {
  background: #f0f7fc;
  border-color: #003d63;
}

.back-to-shop-btn:active {
  background: #e0f0f9;
}

.ehr-checkout-buttons {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  z-index: 10;
  background: #f9fbfd;
}

.ehr-checkout-buttons :deep(.new-library-button) {
  height: 42px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.43px;
  background: #f9fbfd;
}

.alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.alert-content span {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 18px;
}

.alert-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  flex-shrink: 0;
}

.alert-close:hover {
  opacity: 1;
}

/* Promo Code Styles */
.promo-code-section {
  font-family: 'Inter', sans-serif;
}

.promo-input-wrapper {
  position: relative;
}

.promo-input {
  width: 100%;
  height: 36px;
  padding: 0 32px 0 12px;
  border: 1px solid #c4c4c4;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #333;
  background: #fff;
  outline: none;
  box-sizing: border-box;
}

.promo-input:focus {
  border-color: #7f9bb3;
}

.promo-input:disabled {
  background: #f5f5f5;
  color: #999;
}

.promo-status-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: bold;
}

.status-success {
  color: #84BC3B;
}

.status-error {
  color: #EC2A42;
}

.promo-apply-btn {
  height: 36px;
  min-width: 72px;
  padding: 0 16px;
  border: 1px solid #004879;
  border-radius: 4px;
  background: #fff;
  color: #004879;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  white-space: nowrap;
}

.promo-apply-btn:hover {
  background: #f0f7fc;
}

.promo-apply-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.promo-remove-btn {
  height: 36px;
  min-width: 72px;
  padding: 0 16px;
  border: 1px solid #004879;
  border-radius: 4px;
  background: #fff;
  color: #004879;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  white-space: nowrap;
}

.promo-remove-btn:hover {
  background: #f0f7fc;
}

.bvibrant-link {
  cursor: pointer;
  text-decoration: underline;
  color: #004879;
}

.bvibrant-link:hover {
  color: #003d63;
}
</style>
